import React, { useState } from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { ModalDialog, ModalType } from '../../components/ModalDialog/ModalDialog';
import { TLSSettingsPage } from '../TLSSettings/TLSSettingsPage';
import { PermissionsSetting } from '../../customSettings/PermissionsSetting/PermissionsSetting';
import { PRIVACY_SECURITY_SETTING_IDS } from '../../config/SettingIds';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import { SettingsCategory } from '../../config/SettingsCategory';
import { useSettingsPage } from '../../hooks/useSettingsPage';
import { SettingsRenderer } from '../../SettingsProvider/SettingsRenderer';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';

// Navigation views for Privacy & Security Settings
export enum PrivacySecuritySettingsView {
	PrivacySecurity = 'privacy-security',
	TLS = 'tls',
}

interface PrivacySecuritySettingsPageProps {
	title: string;
	description?: string;
}

export const PrivacySecuritySettingsPage: React.FC<PrivacySecuritySettingsPageProps> = ({
	title,
}) => {
	const [showDeletePasswordsModal, setShowDeletePasswordsModal] = React.useState(false);
	const [currentView, setCurrentView] = useState<PrivacySecuritySettingsView>(
		PrivacySecuritySettingsView.PrivacySecurity
	);

	const { settingsState, availableSettingIds, handleChangeBySettingId } = useSettingsPage(
		{
			category: SettingsCategory.PrivacySecurity,
			revertOnError: true,
		}
	);

	// Navigation functions
	const navigateToTLSSettingsView = () => {
		setCurrentView(PrivacySecuritySettingsView.TLS);
	};

	const navigateToPrivacySecuritySettingsView = () => {
		setCurrentView(PrivacySecuritySettingsView.PrivacySecurity);
	};

	const handleConfirmDeletePasswords = async () => {
		try {
			const nativeBridge = SettingsNativeBridgeFactory.getBridge();
			if (nativeBridge) {
				await nativeBridge.setSetting({
					settingKey: PRIVACY_SECURITY_SETTING_IDS.TRIGGER_DELETE_PASSWORDS,
					settingValue: true,
				});
			}
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				'Passwords deleted successfully',
				AotCategory.Info,
				'Passwords deleted successfully'
			);
			setShowDeletePasswordsModal(false);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Error deleting passwords: ${error}`,
				AotCategory.Error,
				`Error deleting passwords: ${error}`
			);
			alert('Failed to delete passwords. Please try again.');
		}
	};

	// Navigation handlers map for navigation settings
	const navigationHandlers = {
		[PRIVACY_SECURITY_SETTING_IDS.TLS_Settings]: navigateToTLSSettingsView,
	};

	// Use SettingsRenderer for all settings
	const renderSettingById = (settingId: string) => {
		if (settingId === PRIVACY_SECURITY_SETTING_IDS.PERMISSIONS) {
			return <PermissionsSetting settingId={PRIVACY_SECURITY_SETTING_IDS.PERMISSIONS} />;
		}
		return SettingsRenderer({
			settingId,
			settingsState,
			handleChange: async (id, value) => {
				// Intercept delete passwords button to show modal
				if (id === PRIVACY_SECURITY_SETTING_IDS.TRIGGER_DELETE_PASSWORDS) {
					setShowDeletePasswordsModal(true);
				} else {
					await handleChangeBySettingId(id, value);
				}
			},
			navigationHandlers,
		});
	};

	// Configuration map for different views
	const viewConfig = {
		[PrivacySecuritySettingsView.TLS]: {
			title: 'TLS',
			onNavigatingBack: navigateToPrivacySecuritySettingsView,
		},
	};

	// Handle view navigation
	if (currentView === PrivacySecuritySettingsView.TLS) {
		const config = viewConfig[PrivacySecuritySettingsView.TLS];
		return <TLSSettingsPage onNavigatingBack={config.onNavigatingBack} />;
	}

	return (
		<>
			<SettingsPage
				title={title}
				settings={
					<>
						{availableSettingIds.map((settingId) => (
							<React.Fragment key={settingId}>
								{renderSettingById(settingId)}
							</React.Fragment>
						))}
					</>
				}
			/>
			{/* Delete Passwords Modal */}
			<ModalDialog
				isOpen={showDeletePasswordsModal}
				onClose={() => setShowDeletePasswordsModal(false)}
				type={ModalType.Warning}
				title="Delete passwords?"
				description="This action does not remove your store, only your saved credentials."
				primaryButton={{
					buttonText: 'Delete',
					onClick: handleConfirmDeletePasswords,
				}}
				secondaryButton={{
					buttonText: 'Cancel',
					onClick: () => setShowDeletePasswordsModal(false),
				}}
			/>
		</>
	);
};
